import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import './Products.css';

function Products() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    unitPrice: '',
    currency: 'EUR',
    active: true
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await api.get('/products');
      setProducts(response.data.data);
    } catch (error) {
      console.error('Ürünler yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProduct) {
        await api.put(`/products/${editingProduct.id}`, formData);
        alert('Ürün güncellendi!');
      } else {
        await api.post('/products', formData);
        alert('Ürün eklendi!');
      }
      setShowModal(false);
      setEditingProduct(null);
      resetForm();
      fetchProducts();
    } catch (error) {
      alert('Hata: ' + (error.response?.data?.message || 'İşlem başarısız'));
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      code: product.code,
      name: product.name,
      description: product.description || '',
      unitPrice: product.unit_price,
      currency: product.currency,
      active: product.active
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu ürünü silmek istediğinizden emin misiniz?')) return;
    try {
      await api.delete(`/products/${id}`);
      alert('Ürün silindi!');
      fetchProducts();
    } catch (error) {
      alert('Hata: ' + (error.response?.data?.message || 'Silinemedi'));
    }
  };

  const handleToggleActive = async (id, currentStatus) => {
    try {
      await api.put(`/products/${id}`, { active: !currentStatus });
      fetchProducts();
    } catch (error) {
      alert('Durum değiştirilemedi');
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      description: '',
      unitPrice: '',
      currency: 'EUR',
      active: true
    });
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingProduct(null);
    resetForm();
  };

  return (
    <div className="products-page">
      <div className="page-header">
        <h1>📦 Ürün Yönetimi</h1>
        <div>
          <button className="btn-add" onClick={() => setShowModal(true)}>
            + Yeni Ürün
          </button>
          <button className="btn-back" onClick={() => navigate('/dashboard')}>
            ← Geri
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading">Yükleniyor...</div>
      ) : (
        <div className="products-table">
          <table>
            <thead>
              <tr>
                <th>Kod</th>
                <th>Ürün Adı</th>
                <th>Açıklama</th>
                <th>Birim Fiyat</th>
                <th>Para Birimi</th>
                <th>Durum</th>
                <th>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {products.map(product => (
                <tr key={product.id}>
                  <td>{product.code}</td>
                  <td>{product.name}</td>
                  <td>{product.description}</td>
                  <td>{parseFloat(product.unit_price).toFixed(2)}</td>
                  <td>{product.currency}</td>
                  <td>
                    <span 
                      className={`status-badge ${product.active ? 'active' : 'inactive'}`}
                      onClick={() => handleToggleActive(product.id, product.active)}
                      style={{ cursor: 'pointer' }}
                    >
                      {product.active ? 'Aktif' : 'Pasif'}
                    </span>
                  </td>
                  <td>
                    <button className="btn-edit" onClick={() => handleEdit(product)}>
                      ✏️ Düzenle
                    </button>
                    <button className="btn-delete" onClick={() => handleDelete(product.id)}>
                      🗑️ Sil
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingProduct ? 'Ürün Düzenle' : 'Yeni Ürün Ekle'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Ürün Kodu *</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({...formData, code: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label>Ürün Adı *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label>Açıklama</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows="3"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Birim Fiyat *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.unitPrice}
                    onChange={(e) => setFormData({...formData, unitPrice: e.target.value})}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Para Birimi *</label>
                  <select
                    value={formData.currency}
                    onChange={(e) => setFormData({...formData, currency: e.target.value})}
                  >
                    <option value="EUR">EUR</option>
                    <option value="USD">USD</option>
                    <option value="TRY">TRY</option>
                    <option value="GBP">GBP</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => setFormData({...formData, active: e.target.checked})}
                  />
                  Aktif
                </label>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={handleCloseModal}>
                  İptal
                </button>
                <button type="submit" className="btn-save">
                  {editingProduct ? 'Güncelle' : 'Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Products;
